import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Crosshair, CaretDown, CaretRight } from '@phosphor-icons/react';
import { targetAPI } from '../../utils/api';
import { TargetOptInsPanel } from '../show/TargetPanel';

/**
 * TargetsTab — Manage-Entries tab that lists every Target Race on the show
 * with an expandable participants panel per target. Reuses the same
 * <TargetOptInsPanel> the producer sees on ShowDetail so the two views stay
 * in sync (add/remove/mark paid all round-trip through the same endpoints).
 */
export const TargetsTab = ({ showId, show }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await targetAPI.list(showId);
      setTargets(data || []);
      // Auto-expand the first target so producers land on something useful.
      if ((data || []).length > 0) {
        setExpandedId((prev) => prev || data[0].id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load targets');
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const classMap = {};
  ((show && show.classes) || []).forEach((c) => { classMap[c.id] = c; });

  if (loading && targets.length === 0) {
    return <div className="py-8 text-center text-[#9E9E9E] text-sm">Loading targets…</div>;
  }
  if (!loading && targets.length === 0) {
    return (
      <div className="bg-[#161616] border border-[#262626] rounded-lg p-8 text-center" data-testid="targets-tab-empty">
        <Crosshair size={36} className="text-[#262626] mx-auto mb-3" />
        <p className="text-[#9E9E9E] text-sm">No target races configured for this show</p>
        <p className="text-[#555] text-xs mt-1">Create one from the Show Detail page.</p>
      </div>
    );
  }

  return (
    <div data-testid="targets-tab" className="space-y-3">
      {targets.map((tgt) => {
        const isOpen = expandedId === tgt.id;
        const catCount = (tgt.categories || []).length || 1;
        const classIds = tgt.selected_class_ids || [];
        return (
          <div key={tgt.id} className="bg-[#161616] border border-[#262626] rounded-lg overflow-hidden" data-testid={`targets-tab-card-${tgt.id}`}>
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : tgt.id)}
              data-testid={`targets-tab-toggle-${tgt.id}`}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#0A0A0A]/40 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                {isOpen ? <CaretDown size={16} className="text-[#FF375F] flex-shrink-0" /> : <CaretRight size={16} className="text-[#9E9E9E] flex-shrink-0" />}
                <Crosshair size={18} weight="duotone" className="text-[#FF375F] flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-white truncate" style={{ fontFamily: 'Outfit' }}>{tgt.name}</h3>
                  <p className="text-[11px] text-[#9E9E9E] mt-0.5">
                    {catCount} categor{catCount === 1 ? 'y' : 'ies'} · {classIds.length} class{classIds.length === 1 ? '' : 'es'}
                    {Number(tgt.entry_fee) > 0 && (
                      <span> · <span className="text-[#34C759] font-semibold">${Number(tgt.entry_fee).toFixed(0)}</span> per rider+horse</span>
                    )}
                    <span> · {tgt.places_paid} places paid</span>
                  </p>
                </div>
              </div>
            </button>
            {isOpen && (
              <TargetOptInsPanel show={{ id: showId, ...show }} target={tgt} onChange={() => { /* noop — panel self-loads */ }} />
            )}
          </div>
        );
      })}
    </div>
  );
};
